import { motion } from 'framer-motion';
import { FileText, AlertCircle, Ban, Camera, Shield, Scale } from 'lucide-react';

export default function RulesTerms() {
  return (
    <div className="min-h-screen bg-ff-blue pt-20" data-testid="rules-terms-page">
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="badge mb-4">
              Event Details
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-ff-brown">
              Rules, Terms & <span className="text-ff-brown">Conditions</span>
            </h1>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* No Dickheads Policy */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8 border-l-4 border-ff-secondary">
              <div className="flex items-center gap-4 mb-6">
                <Shield className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">No Dickheads Policy</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                Frequency Forest operates a strict <strong className="text-ff-brown">NO DICKHEADS</strong> policy. 
                We want everyone to have an amazing time, and that means treating each other with 
                respect. There is absolutely zero tolerance for:
              </p>
              <ul className="text-ff-brown/70 space-y-2 ml-4">
                <li>• Harassment, bullying, or intimidation of any kind</li>
                <li>• Violence or threatening behavior</li>
                <li>• Sexual harassment or assault</li>
                <li>• Discrimination based on race, gender, sexuality, or any other factor</li>
                <li>• Disrespectful behavior towards staff, volunteers, or fellow attendees</li>
              </ul>
              <p className="text-ff-brown/70 mt-4 font-medium">
                Anyone engaging in such behavior will be removed from the event immediately 
                without refund and may be banned from future events.
              </p>
            </div>

            {/* Leave No Trace */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <h2 className="font-display text-2xl text-ff-brown mb-4">Leave No Trace</h2>
              <p className="text-ff-brown/70">
                Whatever you bring in, you take out. This is non-negotiable. We are guests of 
                this land and must treat it with the utmost respect. Leave your campsite cleaner 
                than you found it. Dispose of waste properly. Respect the natural environment.
              </p>
            </div>

            {/* Prohibited Items */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Ban className="w-8 h-8 text-red-500" />
                <h2 className="font-display text-2xl text-ff-brown">Prohibited Items</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                The following items are strictly prohibited and will be confiscated at the gate:
              </p>
              <div className="grid md:grid-cols-2 gap-4 text-ff-brown/70">
                <ul className="space-y-2">
                  <li>• Alcohol & alcoholic drinks</li>
                  <li>• Glass of any kind</li>
                  <li>• Illegal substances</li>
                  <li>• Weapons (including knives)</li>
                  <li>• Fireworks, flares, or explosives</li>
                  <li>• Open fire equipment (fire pits, BBQs)</li>
                  <li>• Drones or UAVs</li>
                </ul>
                <ul className="space-y-2">
                  <li>• Large sound systems</li>
                  <li>• Generators</li>
                  <li>• Pets (except service animals)</li>
                  <li>• Unauthorized commercial goods</li>
                  <li>• Spray paint or graffiti materials</li>
                  <li>• Laser pointers</li>
                </ul>
              </div>
            </div>

            {/* Entry Conditions */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <FileText className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Entry Conditions</h2>
              </div>
              <ul className="text-ff-brown/70 space-y-3">
                <li>• Valid ticket and matching photo ID required for entry</li>
                <li>• Wristbands must be worn at all times and not removed</li>
                <li>• Vehicles and bags will be searched upon entry</li>
                <li>• Management reserves the right to refuse entry</li>
                <li>• Re-entry is not permitted. Exception: non-camping ticket holders may leave and return,
                    but no same-day pass-outs – you may only re-enter on the following day</li>
                <li>• This is a strictly 18+ event – valid photo ID required</li>
              </ul>
            </div>

            {/* Ticket Conditions */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Scale className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Ticket Conditions</h2>
              </div>
              <ul className="text-ff-brown/70 space-y-3">
                <li>• Tickets are non-refundable but may be transferred via official channels</li>
                <li>• Reselling tickets above face value is prohibited</li>
                <li>• The event organizers reserve the right to make changes to the lineup, 
                    venue, dates, or program without notice</li>
                <li>• In the event of cancellation due to unforeseen circumstances, ticket 
                    holders will be notified and refunds processed according to our policy</li>
                <li>• Lost or stolen tickets may be replaced at the discretion of management</li>
              </ul>
            </div>

            {/* Photography & Media */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <Camera className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Photography & Media</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                By attending Frequency Forest, you consent to being photographed, filmed, or 
                recorded. This footage may be used for promotional purposes including social 
                media, marketing materials, and future event promotion.
              </p>
              <p className="text-ff-brown/70">
                Personal photography and video for non-commercial use is permitted. Professional 
                photography equipment and commercial filming require prior approval – please 
                apply through our Media application.
              </p>
            </div>

            {/* Liability */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 mb-8">
              <div className="flex items-center gap-4 mb-6">
                <AlertCircle className="w-8 h-8 text-ff-brown" />
                <h2 className="font-display text-2xl text-ff-brown">Safety & Liability</h2>
              </div>
              <p className="text-ff-brown/70 mb-4">
                Attendees enter the event at their own risk. The organizers accept no responsibility 
                for loss, damage, or injury to persons or property.
              </p>
              <ul className="text-ff-brown/70 space-y-2">
                <li>• You are responsible for your own safety and the safety of your belongings</li>
                <li>• Follow all safety instructions from staff and signage</li>
                <li>• The venue includes natural terrain – appropriate footwear is recommended</li>
                <li>• Be aware of weather conditions and prepare accordingly</li>
                <li>• Report any unsafe conditions to staff immediately</li>
              </ul>
            </div>

            {/* Agreement */}
            <div className="bg-white/40 backdrop-blur-sm rounded-3xl p-8 border-l-4 border-ff-secondary">
              <h3 className="font-display text-xl text-ff-brown mb-4">By Attending, You Agree</h3>
              <p className="text-ff-brown/70">
                By purchasing a ticket and attending Frequency Forest, you agree to abide by 
                these rules, terms, and conditions. Failure to comply may result in removal 
                from the event without refund. These terms may be updated – ticket holders 
                will be notified of any significant changes.
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
